<template>
  <div class="feedback-management">
    <!-- 页面标题 -->
    <div class="page-header">
      <div class="header-left">
        <h2 class="page-title">意见反馈管理</h2>
        <p class="page-subtitle">查看和处理用户反馈</p>
      </div>
    </div>

    <!-- 筛选区域 -->
    <div class="filter-section">
      <el-card shadow="never">
        <div class="filter-content">
          <!-- 状态筛选 -->
          <div class="quick-filters">
            <el-radio-group v-model="filterParams.status" @change="handleFilterChange">
              <el-radio-button label="">全部反馈</el-radio-button>
              <el-radio-button label="pending">待处理</el-radio-button>
              <el-radio-button label="processing">处理中</el-radio-button>
              <el-radio-button label="completed">已完成</el-radio-button>
            </el-radio-group>
          </div>

          <!-- 高级筛选 -->
          <div class="advanced-filters">
            <el-select 
              v-model="filterParams.type" 
              placeholder="反馈类型"
              clearable
              @change="handleFilterChange"
              style="width: 150px"
            >
              <el-option label="功能异常" value="功能异常" />
              <el-option label="体验问题" value="体验问题" />
              <el-option label="功能建议" value="功能建议" />
              <el-option label="其他" value="其他" />
            </el-select>

            <el-input
              v-model="filterParams.search"
              placeholder="搜索内容或用户昵称"
              clearable
              @clear="handleSearch"
              style="width: 250px"
            >
              <template #append>
                <el-button @click="handleSearch" icon="Search" />
              </template>
            </el-input>

            <el-button @click="resetFilters">重置</el-button>
          </div>
        </div>
      </el-card>
    </div>

    <!-- 反馈列表 -->
    <div class="list-section">
      <el-card shadow="never">
        <el-table 
          :data="feedbackList" 
          style="width: 100%"
          v-loading="loading"
        >
          <el-table-column prop="id" label="ID" width="60" />
          
          <el-table-column label="用户" width="150">
            <template #default="{ row }">
              <div class="user-info">
                <el-avatar :size="32" :src="row.userAvatar" />
                <span class="user-nickname">{{ row.userNickname }}</span>
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="type" label="类型" width="100">
            <template #default="{ row }">
              <el-tag :type="getTypeTagType(row.type)" size="small">
                {{ row.type }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column label="反馈内容" min-width="300">
            <template #default="{ row }">
              <div class="feedback-content">
                <p class="content-text">{{ row.content }}</p>
                <div v-if="row.images && row.images.length > 0" class="image-preview">
                  <el-image
                    v-for="(img, index) in row.images"
                    :key="index"
                    :src="`/uploads${img}`"
                    :preview-src-list="row.images.map(i => `/uploads${i}`)"
                    :initial-index="index"
                    fit="cover"
                    style="width: 60px; height: 60px; margin-right: 8px; border-radius: 4px;"
                  />
                </div>
                <p v-if="row.contact" class="contact-info">
                  <el-icon><Phone /></el-icon>
                  {{ row.contact }}
                </p>
              </div>
            </template>
          </el-table-column>

          <el-table-column prop="status" label="状态" width="100">
            <template #default="{ row }">
              <el-tag :type="getStatusTagType(row.status)" size="small">
                {{ getStatusLabel(row.status) }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column prop="createdAt" label="提交时间" width="160">
            <template #default="{ row }">
              {{ formatDateTime(row.createdAt) }}
            </template>
          </el-table-column>

          <el-table-column label="操作" width="100" fixed="right">
            <template #default="{ row }">
              <el-button 
                type="primary" 
                size="small" 
                @click="handleReply(row)"
              >
                {{ row.reply ? '查看回复' : '回复' }}
              </el-button>
            </template>
          </el-table-column>
        </el-table>

        <!-- 分页 -->
        <div class="pagination-section">
          <el-pagination
            v-model:current-page="pagination.page"
            v-model:page-size="pagination.limit"
            :page-sizes="[10, 20, 50, 100]"
            :total="pagination.total"
            layout="total, sizes, prev, pager, next, jumper"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
          />
        </div>
      </el-card>
    </div>

    <!-- 回复对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="currentFeedback?.reply ? '查看回复' : '回复反馈'"
      width="600px"
    >
      <div v-if="currentFeedback">
        <el-form :model="replyForm" label-width="80px">
          <el-form-item label="反馈内容">
            <div class="readonly-content">{{ currentFeedback.content }}</div>
          </el-form-item>

          <el-form-item label="处理状态">
            <el-select v-model="replyForm.status" style="width: 100%">
              <el-option label="待处理" value="pending" />
              <el-option label="处理中" value="processing" />
              <el-option label="已完成" value="completed" />
            </el-select>
          </el-form-item>

          <el-form-item label="回复内容">
            <el-input
              v-model="replyForm.reply"
              type="textarea"
              :rows="5"
              placeholder="请输入回复内容..."
            />
          </el-form-item>
        </el-form>
      </div>

      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleSubmitReply">提交</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import { Search, Phone } from '@element-plus/icons-vue'
import api from '../utils/api'

// 响应式数据
const loading = ref(false)
const dialogVisible = ref(false)
const feedbackList = ref([])
const currentFeedback = ref(null)

const filterParams = reactive({
  status: '',
  type: '',
  search: ''
})

const pagination = reactive({
  page: 1,
  limit: 20,
  total: 0
})

const replyForm = reactive({
  status: '',
  reply: ''
})

// 加载反馈列表
const loadFeedbacks = async () => {
  loading.value = true
  try {
    const params = {
      page: pagination.page,
      limit: pagination.limit,
      status: filterParams.status,
      type: filterParams.type,
      search: filterParams.search
    }

    const response = await api.getFeedbacks(params)
    
    if (response.success) {
      feedbackList.value = response.data.list
      pagination.total = response.data.total
    }
  } catch (error) {
    console.error('加载反馈列表失败:', error)
    ElMessage.error('加载反馈列表失败')
  } finally {
    loading.value = false
  }
}

// 筛选变化
const handleFilterChange = () => {
  pagination.page = 1
  loadFeedbacks()
}

// 搜索
const handleSearch = () => {
  pagination.page = 1
  loadFeedbacks()
}

// 重置筛选
const resetFilters = () => {
  filterParams.status = ''
  filterParams.type = ''
  filterParams.search = ''
  pagination.page = 1
  loadFeedbacks()
}

// 分页变化
const handleSizeChange = (val) => {
  pagination.limit = val
  pagination.page = 1
  loadFeedbacks()
}

const handleCurrentChange = (val) => {
  pagination.page = val
  loadFeedbacks()
}

// 打开回复对话框
const handleReply = (feedback) => {
  currentFeedback.value = feedback
  replyForm.status = feedback.status
  replyForm.reply = feedback.reply || ''
  dialogVisible.value = true
}

// 提交回复
const handleSubmitReply = async () => {
  try {
    const response = await api.updateFeedback(currentFeedback.value.id, {
      status: replyForm.status,
      reply: replyForm.reply
    })

    if (response.success) {
      ElMessage.success('回复成功')
      dialogVisible.value = false
      loadFeedbacks()
    }
  } catch (error) {
    console.error('回复失败:', error)
    ElMessage.error('回复失败')
  }
}

// 辅助函数
const getTypeTagType = (type) => {
  const typeMap = {
    '功能异常': 'danger',
    '体验问题': 'warning',
    '功能建议': 'success',
    '其他': 'info'
  }
  return typeMap[type] || 'info'
}

const getStatusLabel = (status) => {
  const labelMap = {
    'pending': '待处理',
    'processing': '处理中',
    'completed': '已完成'
  }
  return labelMap[status] || status
}

const getStatusTagType = (status) => {
  const typeMap = {
    'pending': 'warning',
    'processing': '',
    'completed': 'success'
  }
  return typeMap[status] || 'info'
}

const formatDateTime = (dateStr) => {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  return date.toLocaleString('zh-CN')
}

// 组件挂载时加载数据
onMounted(() => {
  loadFeedbacks()
})
</script>

<style scoped>
.feedback-management {
  padding: 20px;
}

.page-header {
  margin-bottom: 24px;
}

.page-title {
  font-size: 24px;
  font-weight: 600;
  color: #303133;
  margin: 0 0 8px 0;
}

.page-subtitle {
  font-size: 14px;
  color: #909399;
  margin: 0;
}

.filter-section {
  margin-bottom: 24px;
}

.filter-content {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.quick-filters {
  display: flex;
  align-items: center;
}

.advanced-filters {
  display: flex;
  gap: 12px;
  align-items: center;
}

.user-info {
  display: flex;
  align-items: center;
  gap: 8px;
}

.user-nickname {
  font-size: 14px;
  color: #303133;
}

.feedback-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.content-text {
  margin: 0;
  font-size: 14px;
  color: #606266;
  line-height: 1.6;
}

.image-preview {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.contact-info {
  margin: 0;
  font-size: 12px;
  color: #909399;
  display: flex;
  align-items: center;
  gap: 4px;
}

.readonly-content {
  padding: 12px;
  background-color: #f5f7fa;
  border-radius: 4px;
  font-size: 14px;
  line-height: 1.6;
  color: #606266;
}

.pagination-section {
  margin-top: 20px;
  display: flex;
  justify-content: center;
}
</style>
